import { z } from 'zod';

/**
 * How the study is fielded. A simulation still fields a real conversation with a real
 * guide — the participants are generated rather than recruited — which is why it is a
 * launch mode and not a separate lifecycle.
 *
 * It lives here rather than beside the launch port because it is a column on the
 * provisioning record and a field on the job that starts a run, so the port layer is not
 * the only thing that has to spell it. A second list would be one the two could disagree
 * about, and the direction of that disagreement is a simulation fielded as a recruit study.
 */
export const LAUNCH_MODES = ['recruit', 'simulation'] as const;
export const launchModeSchema = z.enum(LAUNCH_MODES);
export type LaunchMode = z.infer<typeof launchModeSchema>;

/**
 * The ordering that carries a study into Remesh, as states rather than as a convention.
 *
 * Remesh has no transaction across these calls and one of them cannot be undone, so the
 * order is not a style preference: metadata and the guide are writable only until an
 * audience converts the conversation, and everything that has to be true of the study is
 * therefore true before `apply_audience` runs. Encoding the order as a persisted position
 * is what makes it survive a crash — a resumed run reads where it was rather than
 * inferring it from whatever Remesh happens to answer.
 *
 * `lock` sits after `verify` and before `launch` for the reason the study lifecycle keeps
 * the two apart: the artifacts must be frozen before the conversation their responses will
 * belong to starts fielding, and a launch that fails then leaves a locked, still-`ready`
 * study this machine can resume rather than a live one with nothing behind it.
 */
export const PROVISIONING_STEPS = [
  'create_conversation',
  'set_metadata',
  'build_guide',
  'apply_audience',
  'verify',
  'lock',
  'launch',
  'go_live',
] as const;
export const provisioningStepSchema = z.enum(PROVISIONING_STEPS);
export type ProvisioningStep = z.infer<typeof provisioningStepSchema>;

/**
 * What a provisioning record holds: the step to run next, or one of the two ways a run
 * stops having a next step.
 *
 * The column carries the step still to be done rather than the last one finished, so a
 * resume is a read and not an arithmetic. The consequence is deliberate and is what every
 * step's idempotency is for: a crash between a Remesh call and the write that records it
 * leaves the position naming a step whose effect already landed, so each step either
 * re-executes harmlessly or checks first.
 */
export const PROVISIONING_STATES = [...PROVISIONING_STEPS, 'complete', 'aborted'] as const;
export const provisioningStateSchema = z.enum(PROVISIONING_STATES);
export type ProvisioningState = z.infer<typeof provisioningStateSchema>;

const STEP_ORDER: Record<ProvisioningStep, number> = Object.fromEntries(
  PROVISIONING_STEPS.map((step, index) => [step, index]),
) as Record<ProvisioningStep, number>;

export const provisioningStepIndex = (step: ProvisioningStep): number => STEP_ORDER[step];

/** Where a run sits when it has done nothing at all. */
export const FIRST_PROVISIONING_STEP: ProvisioningStep = 'create_conversation';

export const isProvisioningStep = (state: ProvisioningState): state is ProvisioningStep =>
  state in STEP_ORDER;

/**
 * Where the run moves once a step has landed. The last step answers `complete` rather
 * than a ninth step, so "nothing left to do" is a state and not the absence of one.
 */
export const nextProvisioningState = (step: ProvisioningStep): ProvisioningState =>
  PROVISIONING_STEPS[provisioningStepIndex(step) + 1] ?? 'complete';

/**
 * The writes the one-way door shuts. Both are refused by Remesh from the moment an
 * audience converts the conversation, and both are refused here first — see
 * `isProvisioningWriteRefused`.
 */
export const POST_CONVERSION_REFUSED_STEPS: readonly ProvisioningStep[] = [
  'set_metadata',
  'build_guide',
];

/**
 * Whether the layer must refuse a metadata or guide write outright, from what it already knows,
 * before a request goes out.
 *
 * The question is whether the audience has been applied — not where the run's cursor sits. A
 * position is a cursor and a cursor can be moved: a retried job, a hand-edited row or a caller
 * asking for a step out of order would all reopen a door that Remesh has permanently shut, and
 * the write would then be refused by Remesh instead of here, which is the outcome this exists to
 * avoid. The apply step records the fact when it lands, so the refusal outlives the position.
 *
 * This is the cheapest of three layers rather than the only one: the ports refuse the same two
 * writes from their own read of the conversation, Remesh refuses them again with the lockout 400,
 * and a guide write that raced a conversion is reported after the fact because no client-side
 * check can prevent it. This one is first because it is the only one that costs no request — and
 * the others are still needed, since this side cannot know about a conversion it did not perform.
 */
export const isProvisioningWriteRefused = ({
  audienceApplied,
  step,
}: {
  audienceApplied: boolean;
  step: ProvisioningStep;
}): boolean => audienceApplied && POST_CONVERSION_REFUSED_STEPS.includes(step);

/**
 * Where a Sigora question ended up in the Remesh guide, as it is stored.
 *
 * Kept because it cannot be recovered: it is the only record of which Remesh message
 * carries which question, and once an audience is applied the guide cannot be rewritten to
 * produce it again. Its shape is stated here rather than reused from the port layer because
 * the column outlives the code that wrote it and is parsed on read like every other jsonb.
 */
export const guidePlacementSchema = z.object({
  question_id: z.string().min(1),
  remesh_message_id: z.string().min(1),
  rank: z.int().min(0),
});
export type StoredGuidePlacement = z.infer<typeof guidePlacementSchema>;

export const guidePlacementsSchema = z.array(guidePlacementSchema);

/**
 * Why a run stopped, in the vocabulary of whatever refused it — a port error code, a
 * domain error code, or the name of a failure this machine raised itself. Stored so the
 * halt is diagnosable without the logs of the process that hit it, which on a resumed run
 * is a different process.
 */
export const provisioningFailureSchema = z.object({
  step: provisioningStepSchema,
  code: z.string().min(1),
  message: z.string().min(1),
  detail: z.record(z.string(), z.unknown()).default({}),
  /**
   * Whether whatever ran this study is going to run it again on its own.
   *
   * A step that throws is how the run tells its queue to retry, and that queue retries over hours,
   * so for most of the life of a recorded failure a run is still coming. Without this the record
   * cannot tell a run that has stopped from one that is between attempts, and everything reading
   * it — the study page, and the claim that lets a researcher restart a run — would offer a second
   * run beside one already scheduled.
   *
   * Defaulted rather than required, because a record written before this existed says nothing
   * about a job that is long gone by now: read as "nothing is coming", which is what makes such a
   * study restartable rather than stuck.
   */
  retryPending: z.boolean().default(false),
});
export type ProvisioningFailure = z.infer<typeof provisioningFailureSchema>;

/**
 * The longest the queue waits between attempts at a run, in seconds. Declared here rather than
 * beside the queue because two places need it: the policy that produces the delay, and the reader
 * that has to decide how long a promised attempt can be outstanding before it stops being believed.
 */
export const PROVISIONING_RETRY_DELAY_MAX_SECONDS = 3600;

/**
 * The longest an attempt can be running, in seconds — the queue's own execution ceiling, which it
 * enforces from the moment the job goes active regardless of heartbeats (they reclaim a dead
 * worker sooner, they do not extend a live one). Declared here for the same reason as the delay:
 * a reader deciding whether a promised attempt is still outstanding has to know both halves.
 */
export const QUEUE_ATTEMPT_MAX_SECONDS = 3600;

/**
 * Room for the queue to notice a job it is due to run: the poll between an attempt becoming due
 * and a worker picking it up, and a worker that is busy with something else when it does.
 */
const RETRY_PROMISE_SLACK_SECONDS = 900;

/**
 * How long after a run last wrote to its record a promised retry stops being believed.
 *
 * `retryPending` is a promise made by whatever held the job, and nothing that reads it can see
 * whether the job still exists. A job can be swept, deleted by hand, or lost to a policy whose
 * limit has been lowered under it — and a promise nobody is going to keep leaves a study reading
 * as a run in progress for ever, with the restart that would fix it refused as a duplicate.
 *
 * Both halves of an attempt are counted, and the waiting half alone is not enough: a run writes to
 * its record when a step finishes or fails, so an attempt that starts after the longest delay and
 * then sits inside one outbound call touches nothing for the delay *plus* however long it runs.
 * Counting only the delay would call such a run stopped while its worker is mid-call, and hand a
 * second worker to a study that already has one — the duplicate this promise exists to prevent,
 * arriving through the bound meant to make it safe.
 */
export const PROVISIONING_RETRY_PROMISE_SECONDS =
  PROVISIONING_RETRY_DELAY_MAX_SECONDS + QUEUE_ATTEMPT_MAX_SECONDS + RETRY_PROMISE_SLACK_SECONDS;

/**
 * Classifications no amount of retrying can get past.
 *
 * A step that throws is how a run asks its queue for another attempt, and that is right for the
 * causes retrying fixes: an unreachable Remesh, a process that came up without its wiring, a 500
 * from something being deployed. These are the other kind — a study whose artifacts are not
 * approved, an audience that moved under the run, a record another run has taken past this one, a
 * conversation whose audience is already applied, a launch this environment has no adapter for.
 * Every one of them refuses again immediately, so retrying spends four hours of the ladder to
 * arrive back where it started, and — because a run between attempts is not a stopped run — does
 * it with the study page showing a step counter and offering nobody a way out.
 *
 * Listed rather than derived, and everything unlisted is retried: this decides whether a paid-for
 * study keeps being tried, so a code nobody has thought about gets the treatment that keeps trying.
 */
const UNRETRYABLE_PROVISIONING_FAILURES: ReadonlySet<string> = new Set([
  'provisioning_position_moved',
  'provisioning_aborted',
  'provisioning_intent_changed',
  'provisioning_study_not_ready',
  'provisioning_verify_failed',
  'provisioning_launch_not_fielding',
  'audience_already_applied',
  'not_publicly_supported',
  'guide_image_missing',
  'guide_image_refused',
  'audience_screening_mismatch',
  'audience_screener_invalid',
  'audience_option_unknown',
  'audience_dimension_unsupported',
  'audience_range_out_of_bounds',
  'audience_filter_shape_mismatch',
  // A policy review is looked up, never asked for, by provisioning: no retry can change the answer.
  'study_refused',
  'policy_review_required',
  'policy_review_pending',
  'policy_review_unavailable',
  // And a study whose Goal is not chosen, which the review cannot be asked for until it is.
  'goal_not_chosen',
]);

/** Whether running the study again could plausibly get further than this failure did. */
export const isRetryableProvisioningFailure = (code: string): boolean =>
  !UNRETRYABLE_PROVISIONING_FAILURES.has(code);

/**
 * The topic that runs a study into Remesh. Sent in the transaction that records a verified payment,
 * one job per paid purchase under the purchase's id, and by a researcher's setup retry; consumed by
 * the worker, which is the only process holding the adapters that can field a study.
 *
 * A queue rather than a call inside the request for the ordinary reason and one particular one:
 * the run makes eight sequential network calls to Remesh, so a request holding it would stand
 * for a minute and lose the run to any client that gave up — and the machine is built to be
 * resumed by a different process than the one that failed, which a request cannot be.
 */
export const QUEUE_STUDY_PROVISION = 'study.provision';

/**
 * Where pg-boss puts a provisioning job that ran out of attempts. Its handler records the paid
 * launch as stopped `interrupted` when no provisioning record says why it stopped, so a run the
 * queue gave up on is one the study page shows stopped and a setup retry can start again.
 */
export const QUEUE_STUDY_PROVISION_DEAD_LETTER = 'study.provision.dead-letter';

/**
 * The whole of what a run needs to start, and every field of it is snapshotted onto the
 * provisioning record at `start`. Nothing here is re-read from the study later, so a redelivered
 * job carrying different words or a different mode is refused or warned about rather than
 * adopted — which is why the payload is small enough to be worth pinning by schema.
 */
export const studyProvisionPayloadSchema = z.object({
  studyId: z.uuid(),
  /**
   * The paid purchase this run fulfils. A stop the run meets before the provisioning record exists
   * is written on that purchase's launch intent, which is the only row that can hold one.
   */
  purchaseId: z.uuid(),
  /**
   * The id this job was sent under. A stop the job writes is its own only while no later send for
   * the purchase exists, so a late or redelivered job cannot stop a launch a retry has restarted.
   */
  sendId: z.uuid(),
  /** What participants read before the first question. */
  introduction: z.string().min(1),
  /** The purchase's launch mode, never configuration's; the worker refuses one it does not run. */
  mode: launchModeSchema,
});
export type StudyProvisionPayload = z.infer<typeof studyProvisionPayloadSchema>;

/** A run as its sender names it; the send adds the id it is sent under as `sendId`. */
export type StudyProvisionRequest = Omit<StudyProvisionPayload, 'sendId'>;
